import { useState, useRef, useCallback, useEffect } from 'react';
import * as Cesium from 'cesium';

/**
 * Walk/FPS navigation mode hook.
 * WASD moves the camera; mouse look via Pointer Lock API.
 *
 * @param {Cesium.Viewer|null} viewer - Cesium viewer instance
 * @returns {{ isWalkMode: boolean, toggleWalkMode: function }}
 */
export function useWalkMode(viewer) {
  const [isWalkMode, setIsWalkMode] = useState(false);

  // Mirror viewer in a ref so callbacks never hold a stale reference
  const viewerRef = useRef(viewer);
  useEffect(() => { viewerRef.current = viewer; }, [viewer]);

  // All mutable walk state in refs to avoid stale closures in event handlers
  const headingRef = useRef(0);
  const pitchRef = useRef(0);
  const smoothDxRef = useRef(0);
  const smoothDyRef = useRef(0);
  const pressedKeysRef = useRef(new Set());
  const lastTimeRef = useRef(null);
  const listenersRef = useRef({});
  const preRenderListenerRef = useRef(null);
  const isWalkModeRef = useRef(false);

  const disableWalkMode = useCallback(() => {
    const v = viewerRef.current;
    if (v && !v.isDestroyed()) {
      // Restore orbit controls
      const controller = v.scene.screenSpaceCameraController;
      controller.enableRotate = true;
      controller.enableTranslate = true;
      controller.enableTilt = true;
      controller.enableZoom = true;

      // Remove preRender movement loop
      if (preRenderListenerRef.current) {
        v.scene.preRender.removeEventListener(preRenderListenerRef.current);
        preRenderListenerRef.current = null;
      }

      // Release pointer lock if we own it
      const canvas = v.canvas;
      if (document.pointerLockElement === canvas) {
        document.exitPointerLock();
      }
    }

    // Remove all document-level listeners
    const { keydown, keyup, mousemove, pointerlockchange } = listenersRef.current;
    if (keydown) document.removeEventListener('keydown', keydown);
    if (keyup) document.removeEventListener('keyup', keyup);
    if (mousemove) document.removeEventListener('mousemove', mousemove);
    if (pointerlockchange) document.removeEventListener('pointerlockchange', pointerlockchange);
    listenersRef.current = {};

    pressedKeysRef.current.clear();
    isWalkModeRef.current = false;
    setIsWalkMode(false);
  }, []);

  const enableWalkMode = useCallback(() => {
    const v = viewerRef.current;
    if (!v || v.isDestroyed()) return;

    // Snapshot current heading/pitch
    headingRef.current = v.camera.heading;
    pitchRef.current = v.camera.pitch;

    // Disable orbit controls so Cesium doesn't fight our movement
    const controller = v.scene.screenSpaceCameraController;
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableTilt = false;
    controller.enableZoom = false;

    const canvas = v.canvas;
    const WALK_KEYS = new Set(['w', 'a', 's', 'd', 'W', 'A', 'S', 'D',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);

    const keydown = (e) => {
      pressedKeysRef.current.add(e.key);
      if (WALK_KEYS.has(e.key)) e.preventDefault();
    };

    const keyup = (e) => {
      pressedKeysRef.current.delete(e.key);
    };

    // Accumulate raw mouse deltas; smoothing is applied in the render loop
    const mousemove = (e) => {
      if (document.pointerLockElement !== canvas) return;
      smoothDxRef.current += e.movementX;
      smoothDyRef.current += e.movementY;
    };

    const pointerlockchange = () => {
      if (document.pointerLockElement !== canvas) {
        disableWalkMode();
      }
    };

    document.addEventListener('keydown', keydown);
    document.addEventListener('keyup', keyup);
    document.addEventListener('mousemove', mousemove);
    document.addEventListener('pointerlockchange', pointerlockchange);
    listenersRef.current = { keydown, keyup, mousemove, pointerlockchange };

    // preRender movement loop
    lastTimeRef.current = null;
    smoothDxRef.current = 0;
    smoothDyRef.current = 0;
    const movementLoop = () => {
      if (document.pointerLockElement !== canvas) return;
      const cv = viewerRef.current;
      if (!cv || cv.isDestroyed()) return;

      const now = Date.now();
      if (lastTimeRef.current === null) {
        lastTimeRef.current = now;
        return;
      }
      const dt = Math.min((now - lastTimeRef.current) / 1000, 0.1); // cap at 100 ms
      lastTimeRef.current = now;

      // Smooth mouse look: exponential low-pass filter on accumulated deltas
      const SENSITIVITY = 0.003;
      const SMOOTH = 0.25; // 0 = no smoothing, 1 = no movement
      smoothDxRef.current *= SMOOTH;
      smoothDyRef.current *= SMOOTH;
      if (Math.abs(smoothDxRef.current) > 0.01 || Math.abs(smoothDyRef.current) > 0.01) {
        headingRef.current += smoothDxRef.current * SENSITIVITY;
        pitchRef.current -= smoothDyRef.current * SENSITIVITY;
        const MAX_PITCH = Cesium.Math.toRadians(85);
        pitchRef.current = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitchRef.current));
        cv.camera.setView({
          destination: cv.camera.position,
          orientation: { heading: headingRef.current, pitch: pitchRef.current, roll: 0 },
        });
      }

      const keys = pressedKeysRef.current;
      const fast = keys.has('Shift');
      const speed = 10.0 * (fast ? 8 : 1) * dt;
      const cam = cv.camera;

      if (keys.has('w') || keys.has('W') || keys.has('ArrowUp')) cam.moveForward(speed);
      if (keys.has('s') || keys.has('S') || keys.has('ArrowDown')) cam.moveBackward(speed);
      if (keys.has('a') || keys.has('A') || keys.has('ArrowLeft')) cam.moveLeft(speed);
      if (keys.has('d') || keys.has('D') || keys.has('ArrowRight')) cam.moveRight(speed);
    };

    v.scene.preRender.addEventListener(movementLoop);
    preRenderListenerRef.current = movementLoop;

    canvas.requestPointerLock();
    isWalkModeRef.current = true;
    setIsWalkMode(true);
  }, [disableWalkMode]);

  const toggleWalkMode = useCallback(() => {
    if (isWalkModeRef.current) {
      disableWalkMode();
    } else {
      enableWalkMode();
    }
  }, [enableWalkMode, disableWalkMode]);

  // Clean up on unmount
  useEffect(() => {
    return () => { disableWalkMode(); };
  }, [disableWalkMode]);

  return { isWalkMode, toggleWalkMode };
}
